import React, { useState } from 'react';
import { TextField, Button, Container, Box, Typography, Alert, Link, IconButton, InputAdornment } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import bgVideo from './assets/bglogin.mp4';

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerName, setRegisterName] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  
  // Alternar visibilidad de la contraseña
  const handleTogglePassword = () => setShowPassword(!showPassword);

  // Redirigir según el rol
  const getRedirectPath = (role) => {
    switch (role) {
      case 'admin':
        return '/';
      case 'user':
        return '/';
      case 'client':
        return '/tms'; // Redirigir directamente a TMS
      default:
        return '/';
    }
  };

  // Manejar el envío del formulario de inicio de sesión
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        const data = await response.json();
        // Guardar todos los datos necesarios en localStorage
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('role', data.role);
        localStorage.setItem('userName', data.name); // Añadir el nombre del usuario
        localStorage.setItem('userId', data.id); // Guardar el id del usuario para uso en WMS
        if (onLogin) onLogin(data);
        navigate(getRedirectPath(data.role));
      } else {
        const errorData = await response.json();
        setError(errorData.detail || errorData.msg || 'Error en el inicio de sesión');
      }
    } catch (err) {
      setError('No se pudo conectar al servidor');
    } finally {
      setLoading(false);
    }
  };

  // Manejar el envío del formulario de registro
  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: registerName,
          email: registerEmail,
          password: registerPassword,
        }),
      });

      if (response.ok) {
        setError('Usuario registrado exitosamente. Por favor, inicia sesión.');
        setIsRegistering(false);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || errorData.msg || 'Error en el registro');
      }
    } catch (err) {
      setError('No se pudo conectar al servidor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <video autoPlay loop muted className="background-video">
        <source src={bgVideo} type="video/mp4" />
        Tu navegador no soporta la etiqueta de video.
      </video>

      <Container maxWidth="xs" className="login-box" style={{ zIndex: 10, position: 'relative' }}>
        <Box
          component="form"
          onSubmit={isRegistering ? handleRegisterSubmit : handleLoginSubmit}
          sx={{
            backgroundColor: 'white',
            padding: 4,
            borderRadius: 2,
            boxShadow: 3,
            zIndex: 20,
            position: 'relative',
          }}
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={2}
        >
          <Typography variant="h4" component="h1" align="center" color="textPrimary" gutterBottom>
            {isRegistering ? 'Registro' : 'Bienvenido'}
          </Typography>
          {error && (
            <Alert severity={error.includes('exitosamente') ? 'success' : 'error'}>
              {typeof error === 'string' ? error : JSON.stringify(error)}
            </Alert>
          )}

          {isRegistering ? (
            <>
              <TextField
                label="Nombre"
                variant="outlined"
                fullWidth
                required
                value={registerName}
                onChange={(e) => setRegisterName(e.target.value)}
              />
              <TextField
                label="Email"
                variant="outlined"
                fullWidth
                required
                value={registerEmail}
                onChange={(e) => setRegisterEmail(e.target.value)}
              />
              <TextField
                label="Contraseña"
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                fullWidth
                required
                value={registerPassword}
                onChange={(e) => setRegisterPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleTogglePassword}>
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button type="submit" variant="contained" color="primary" fullWidth disabled={loading}>
                {loading ? 'Registrando...' : 'Registrarse'}
              </Button>
              <Link
                component="button"
                type="button"
                onClick={() => setIsRegistering(false)}
                sx={{ mt: 1 }}
              >
                ¿Ya tienes una cuenta? Inicia sesión
              </Link>
            </>
          ) : (
            <>
              <TextField
                label="Email"
                variant="outlined"
                fullWidth
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <TextField
                label="Contraseña"
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                fullWidth
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleTogglePassword}>
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button type="submit" variant="contained" color="primary" fullWidth disabled={loading}>
                {loading ? 'Iniciando sesión...' : 'Iniciar sesión'}
              </Button>
              <Link
                component="button"
                type="button"
                onClick={() => setIsRegistering(true)}
                sx={{ mt: 1 }}
              >
                ¿No tienes una cuenta? Regístrate
              </Link>
            </>
          )}
        </Box>
      </Container>
    </div>
  );
};

export default Login;